/* eslint-disable @typescript-eslint/no-explicit-any */
import { createContext, ReactNode, useState, useContext, useCallback } from 'react';
import {
	AutomationRuleConditionsContextType,
	ConditionItemType,
	ConditionValidationErrors,
	FieldMeetCertainCriteriaType,
} from './types';
import { randomStringGenerator } from '../utils/random-string-generator';
import { useFieldMeetCertainCriteria } from './field-meet-certain-criteria/use-field-meet-certain-criteria';
import { stringRequired } from '../utils/validations';
import { useAzureProvider } from 'src/azure/context';
import { ChecklistCompletedOrNotCompletedConditionType } from './checklist-completed-or-not-completed/types';
import { useChecklistCompletedOrNotCompletedCondition } from './checklist-completed-or-not-completed/use-checklist-completed-or-not-completed-condition';

export const conditionTypes = {
	fieldMeetACertainCriteria: 'field-meet-a-certain-criteria',
	checklistIsCompleted: 'check-list-is-completed',
	checklistIsNotCompleted: 'check-list-is-not-completed'
};

export const conditionOptions = [
	{
		label: 'Field matches a condition',
		value: conditionTypes.fieldMeetACertainCriteria
	},
	{
		label: 'Checklist is completed',
		value: conditionTypes.checklistIsCompleted
	},
	{
		label: 'Checklist is not completed',
		value: conditionTypes.checklistIsNotCompleted
	}
];

const AutomationRuleConditionsContext = createContext({} as AutomationRuleConditionsContextType);
export const useAutomationRuleConditions = () => useContext(AutomationRuleConditionsContext);

export default function AutomationRuleConditionsProvider(props: {children?: ReactNode | Iterable<ReactNode>}) {
	const {applicationContext} = useAzureProvider();
	const [conditions, setConditions] = useState<Array<ConditionItemType>>([]);
	const [conditionValidationError, setConditionValidationError] = useState<Array<ConditionValidationErrors>>([]);

	const fieldMeetAcertainCriteriaHook = useFieldMeetCertainCriteria();
	const checklistCompletedOrNotCompletedConditionHook = useChecklistCompletedOrNotCompletedCondition();

	const addNewCondition = useCallback(() => {
		setConditions(oldValues => [
			...oldValues,
			{
				id: randomStringGenerator(),
				condition: '',
				created_at: new Date(),
				fieldMeetCertainCriteria: null,
				checklistCompletedOrNotCompletedCondition: null
			}
		]);
	}, []);

	const validate = useCallback(async () => {
		let hasError = false;
		const validationErrors: Array<ConditionValidationErrors> = [];

		const errorsByConditionItem = (conditionItem: ConditionItemType) => {
			return [
				stringRequired(conditionItem.condition as string, 'This field is required.'),
			];
		};

		conditions.forEach((conditionItem) => {
			const conditionError = errorsByConditionItem(conditionItem);
			const errorMsg = conditionError.find(error => error);

			if (errorMsg) {
				hasError = true;
				validationErrors.push({
					conditionId: conditionItem.id,
					condition: errorMsg,
					fieldMeetACertainCriteria: null,
					checklistCompletedOrNotCompletedCondition: null,
				});
			}

			if (
				conditionItem.condition === conditionTypes.fieldMeetACertainCriteria &&
				conditionItem.fieldMeetCertainCriteria
			) {
				const resultValidation = fieldMeetAcertainCriteriaHook.validate(
					conditionItem.fieldMeetCertainCriteria.inputValueType,
					conditionItem.fieldMeetCertainCriteria
				);

				if (resultValidation.hasErrors()) {
					hasError = true;
					validationErrors.push({
						condition: null,
						conditionId: conditionItem.id,
						fieldMeetACertainCriteria: resultValidation.errors,
						checklistCompletedOrNotCompletedCondition: null,
					});
				}
			}

			if (
				(conditionItem.condition === conditionTypes.checklistIsCompleted ||
				conditionItem.condition === conditionTypes.checklistIsNotCompleted) &&
				conditionItem.checklistCompletedOrNotCompletedCondition
			) {
				const resultValidation = checklistCompletedOrNotCompletedConditionHook.validate(conditionItem.checklistCompletedOrNotCompletedCondition);

				if (resultValidation.hasError()) {
					hasError = true;
					validationErrors.push({
						conditionId: conditionItem.id,
						condition: null,
						fieldMeetACertainCriteria: null,
						checklistCompletedOrNotCompletedCondition: resultValidation.errors,
					});
				}
			}
		});

		setConditionValidationError(validationErrors);
		return hasError;
	}, [conditions]);

	const resetValidationErrors = useCallback(() => {
		setConditionValidationError([]);
	}, []);
 
	const getValues = useCallback(() => {
		const mapValues = new Map()
			.set(conditionTypes.fieldMeetACertainCriteria, (item: ConditionItemType) => {
				return fieldMeetAcertainCriteriaHook.getValues(item.fieldMeetCertainCriteria as FieldMeetCertainCriteriaType);
			})
			.set(conditionTypes.checklistIsCompleted, (item: ConditionItemType) => {
				return checklistCompletedOrNotCompletedConditionHook.getValues('check-list-is-completed', item.checklistCompletedOrNotCompletedCondition as ChecklistCompletedOrNotCompletedConditionType);
			})
			.set(conditionTypes.checklistIsNotCompleted, (item: ConditionItemType) => {
				return checklistCompletedOrNotCompletedConditionHook.getValues('check-list-is-not-completed', item.checklistCompletedOrNotCompletedCondition as ChecklistCompletedOrNotCompletedConditionType);
			});

		return conditions.map((item) => {
			const findData = mapValues.get(item.condition);
			return findData ? findData(item) : null;
		});
	}, [conditions]);

	const reset = useCallback(() => {
		setConditions([]);
	}, []);

	const loadConditions = useCallback(async (loadedConditions: Array<{
		type: string;
		values: any;
	}>) => {
		const loadValuesMap = new Map()
			.set(conditionTypes.fieldMeetACertainCriteria, async (
				conditionType: string,
				data: {relationship: string; relationshipCheck: string; values: { field: string; operator: string; value: any; }},
				properties: { id: string; created_at: Date }
			): Promise<ConditionItemType> => {
				return {
					id: properties.id,
					created_at: properties.created_at,
					condition: conditionType,
					fieldMeetCertainCriteria: await fieldMeetAcertainCriteriaHook.loadValues({
						relationshipCheck: data.relationshipCheck as 'any' | 'all',
						relationship: data.relationship,
						field: data.values.field,
						operator: data.values.operator,
						value: data.values.value,
					}),
					checklistCompletedOrNotCompletedCondition: null
				};
			})
			.set(conditionTypes.checklistIsCompleted, async (
				conditionType: string,
				data: {values: ChecklistCompletedOrNotCompletedConditionType},
				properties: { id: string; created_at: Date }
			): Promise<ConditionItemType> => {
				return {
					id: properties.id,
					created_at: properties.created_at,
					condition: conditionType,
					fieldMeetCertainCriteria: null,
					checklistCompletedOrNotCompletedCondition: checklistCompletedOrNotCompletedConditionHook.loadValues(data.values),
				};
			})
			.set(conditionTypes.checklistIsNotCompleted, async (
				conditionType: string,
				data: {values: ChecklistCompletedOrNotCompletedConditionType},
				properties: { id: string; created_at: Date }
			): Promise<ConditionItemType> => {
				return {
					id: properties.id,
					created_at: properties.created_at,
					condition: conditionType,
					fieldMeetCertainCriteria: null,
					checklistCompletedOrNotCompletedCondition: checklistCompletedOrNotCompletedConditionHook.loadValues(data.values),
				};
			});

		const results = await Promise.all(loadedConditions.map(async (conditionItem, idx) => {
			const conditionType = conditionItem.type === 'field-meet-a-certain-criteria-with-multiple-values' ? conditionTypes.fieldMeetACertainCriteria : conditionItem.type;

			const getData = loadValuesMap.get(conditionType);
			const now = new Date();

			return await getData(
				conditionType,
				conditionItem,
				{
					id: randomStringGenerator(),
					created_at: new Date(now.getTime() + idx * 1000),
				});
		}));

		setConditions(results);
	}, [applicationContext]);

	const removeConditionItem = useCallback((id: string) => {
		setConditions(oldConditions => [
			...oldConditions.filter(item => item.id !== id)
		]);
	}, []);

	return (
		<AutomationRuleConditionsContext.Provider
			value={{
				conditions: conditions,
				setConditions: setConditions,
				addNewCondition: addNewCondition,
				validate: validate,
				conditionValidationErrors: conditionValidationError,
				resetValidationErrors: resetValidationErrors,
				getValues: getValues,
				reset,
				loadConditions,
				removeConditionItem
			}}
		>
			{props?.children}
		</AutomationRuleConditionsContext.Provider>
	);
}